import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { generateWorkshopAgenda, mockAgendaInput } from '../data/workshopAgendaLogic';

export const WorkshopAgendaGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [agenda, setAgenda] = useState(null);
  const [activeTab, setActiveTab] = useState('agenda'); // 'agenda', 'facilitator', 'worksheet'

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setAgenda(generateWorkshopAgenda(mockAgendaInput));
      setIsGenerating(false);
    }, 1800);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">AI Workshop Agenda Generator</h1>
          <p className="text-slate-500 mt-1">Skreddersydde pedagogiske opplegg basert på organisasjonsanalyse.</p>
        </div>
        {agenda && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={<SafeIcon name="Download" />}>Eksporter PDF</Button>
            <Button variant="primary" onClick={() => setAgenda(null)} icon={<SafeIcon name="RefreshCw" />}>Ny Konfigurasjon</Button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!agenda ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
            <Card className="border-none shadow-2xl overflow-hidden relative group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full -mr-20 -mt-20 group-hover:scale-110 transition-transform" />
              <CardHeader title="Konfigurer Workshop" subtitle="Hent data fra siste kartlegging" icon={<SafeIcon name="Settings" />} />
              <CardContent className="p-10 space-y-8 relative z-10">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tema</label>
                    <select className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500/20">
                      <option>Teamutvikling</option>
                      <option>Psykologisk Trygghet</option>
                      <option>Stressmestring</option>
                      <option>Konflikthåndtering</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Avdeling</label>
                    <select className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none">
                      <option>Utviklingsavdelingen</option>
                      <option>Salgsteamet</option>
                      <option>Kundeservice</option>
                    </select>
                  </div>
                </div>

                <div className="bg-indigo-50 p-6 rounded-[32px] border border-indigo-100 flex items-center gap-6">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm shrink-0">
                    <SafeIcon name="Zap" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-indigo-900">Koble til Analysedata</h4>
                    <p className="text-xs text-indigo-700/60 leading-relaxed">AI-motoren vil automatisk prioritere øvelser basert på laveste indeksscore for valgt team.</p>
                  </div>
                </div>

                <Button className="w-full py-6 text-lg font-black uppercase tracking-widest" onClick={handleGenerate} disabled={isGenerating}>
                  {isGenerating ? "Designer pedagogisk forløp..." : "Generer Workshop-opplegg"}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            {/* Header Info */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {[
                { label: 'Tittel', val: agenda.meta.title, icon: 'Award', color: 'indigo' },
                { label: 'Varighet', val: agenda.meta.duration, icon: 'Clock', color: 'slate' },
                { label: 'Antall Deltakere', val: `${agenda.meta.teamSize} personer`, icon: 'Users', color: 'slate' },
                { label: 'Hovedmål', val: agenda.meta.goal, icon: 'Target', color: 'emerald' },
              ].map((m, i) => (
                <Card key={i} className={i === 0 ? 'lg:col-span-1 bg-slate-900 text-white border-none' : ''}>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                       <SafeIcon name={m.icon} className={`text-${m.color}-500`} size={14} />
                       <span className="text-[9px] font-black uppercase tracking-widest opacity-50">{m.label}</span>
                    </div>
                    <p className="text-sm font-bold leading-tight">{m.val}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Navigation Tabs */}
            <div className="flex gap-4 p-1 bg-slate-100 rounded-[20px] w-fit">
              {[
                { id: 'agenda', label: 'Workshop Agenda', icon: 'Calendar' },
                { id: 'facilitator', label: 'Fasilitator-guide', icon: 'MessageSquare' },
                { id: 'worksheet', label: 'Deltaker-oppgaver', icon: 'Edit3' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 px-6 py-3 rounded-[18px] text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-white text-indigo-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <SafeIcon name={tab.icon} size={14} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content Area */}
              <div className="lg:col-span-2">
                <Card className="border-none shadow-xl overflow-hidden min-h-[800px]">
                  <CardContent className="p-0">
                    <AnimatePresence mode="wait">
                      {activeTab === 'agenda' && (
                        <motion.div key="agenda" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="p-12 space-y-10">
                          {agenda.agenda.map((step) => (
                            <div key={step.step} className="flex gap-8 relative group">
                              <div className="w-16 shrink-0 flex flex-col items-center">
                                <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-black text-lg shadow-lg group-hover:scale-110 transition-transform">
                                  {step.step}
                                </div>
                                <div className="flex-1 w-0.5 bg-slate-100 my-2" />
                              </div>
                              <div className="flex-1 pb-10">
                                <div className="flex justify-between items-start mb-2">
                                  <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">{step.title}</h3>
                                  <span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-lg text-[10px] font-bold uppercase">{step.time}</span>
                                </div>
                                <p className="text-slate-600 leading-relaxed font-medium">{step.desc}</p>
                                
                                {step.questions && (
                                  <div className="mt-6 grid grid-cols-1 gap-3">
                                    {step.questions.map((q, i) => (
                                      <div key={i} className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-2xl italic text-sm text-indigo-900">
                                        "{q}"
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {step.prompt && (
                                  <div className="mt-6 p-5 bg-emerald-50 border border-emerald-100 rounded-2xl flex gap-4 items-start">
                                    <SafeIcon name="HelpCircle" className="text-emerald-600 shrink-0 mt-1" />
                                    <p className="text-sm font-bold text-emerald-900 italic">"{step.prompt}"</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {activeTab === 'facilitator' && (
                        <motion.div key="facilitator" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-12 space-y-8">
                           <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter mb-8">Fasilitator-guide</h2>
                           {agenda.agenda.map((step) => (
                             <div key={step.step} className="p-6 bg-slate-50 rounded-[32px] border border-slate-100 space-y-3">
                               <div className="flex items-center gap-3">
                                 <div className="w-6 h-6 rounded-lg bg-indigo-600 text-white flex items-center justify-center text-[10px] font-bold">{step.step}</div>
                                 <h4 className="font-bold text-slate-900">{step.title}</h4>
                               </div>
                               <div className="pl-9">
                                 <p className="text-xs font-black text-indigo-600 uppercase tracking-widest mb-1">Ekspert-notat</p>
                                 <p className="text-sm text-slate-600 leading-relaxed italic">"{step.facilitatorNote || "Følg standard metodikk for involvering."}"</p>
                               </div>
                             </div>
                           ))}
                        </motion.div>
                      )}

                      {activeTab === 'worksheet' && (
                        <motion.div key="worksheet" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-12 space-y-12">
                           <div className="text-center space-y-4 border-b border-slate-100 pb-12">
                              <h2 className="text-4xl font-black text-slate-900 uppercase tracking-tighter">Deltaker-oppgave</h2>
                              <p className="text-slate-500 font-medium">{agenda.meta.title} • {agenda.meta.dept}</p>
                           </div>
                           <div className="space-y-10">
                              <div className="space-y-4">
                                <h4 className="font-black text-slate-900 uppercase text-xs tracking-widest">Oppgave: Tiltaksplan</h4>
                                <div className="grid grid-cols-4 gap-2 bg-slate-50 p-2 rounded-2xl">
                                  {['Tiltak', 'Ansvar', 'Frist', 'Suksess'].map(h => (
                                    <div key={h} className="p-3 text-[10px] font-black text-slate-400 uppercase text-center">{h}</div>
                                  ))}
                                  {[1, 2, 3].map(i => (
                                    <React.Fragment key={i}>
                                      <div className="h-16 bg-white border border-slate-100 rounded-xl col-span-1" />
                                      <div className="h-16 bg-white border border-slate-100 rounded-xl col-span-1" />
                                      <div className="h-16 bg-white border border-slate-100 rounded-xl col-span-1" />
                                      <div className="h-16 bg-white border border-slate-100 rounded-xl col-span-1" />
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                           </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar: Logic & Follow-up */}
              <div className="space-y-6">
                <Card className="bg-indigo-600 border-none text-white overflow-hidden relative">
                   <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
                   <CardHeader title="AI Agenda Logic" icon={<SafeIcon name="Zap" className="text-indigo-200" />} />
                   <CardContent className="space-y-4 relative z-10">
                      <p className="text-xs text-indigo-100 leading-relaxed">
                        Dette pedagogiske løpet er automatisk justert fordi teamet skårer lavt på <b>Psykologisk Trygghet</b>. 
                      </p>
                      <div className="p-4 bg-white/10 rounded-2xl border border-white/10">
                         <p className="text-[10px] font-bold text-indigo-200 uppercase mb-2">Pedagogisk grep</p>
                         <p className="text-xs font-medium">Større fokus på anonym refleksjon i steg 3 for å senke terskelen for ytring.</p>
                      </div>
                   </CardContent>
                </Card>

                <Card>
                  <CardHeader title="Anbefalt Oppfølging" icon={<SafeIcon name="Clock" />} />
                  <CardContent className="space-y-4">
                    {agenda.followUp.map((item, i) => (
                      <div key={i} className="flex gap-3 text-xs text-slate-600 items-start">
                        <SafeIcon name="CheckCircle" className="text-emerald-500 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <div className="bg-slate-900 p-8 rounded-[40px] text-white">
                  <SafeIcon name="FileText" className="text-4xl text-indigo-400 mb-6" />
                  <h4 className="text-xl font-black mb-2 uppercase tracking-tighter">Fullstendig Pakke</h4>
                  <p className="text-xs text-slate-400 leading-relaxed mb-6">
                    Last ned komplett manus, presentasjons-slides og utskriftsklare oppgaveark til alle deltakere.
                  </p>
                  <Button variant="primary" className="w-full bg-indigo-600 hover:bg-indigo-500 border-none font-black text-[10px] uppercase py-4">Last ned Workshop-pakke</Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};